import { lazy, Suspense } from 'react';
import { Navigate, Route, BrowserRouter, Routes, useParams } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import type { CellarEntry } from './auth/cellar-list';
import { ActiveCellarProvider, CellarListProvider, useCellarList } from './auth/CellarListContext';
import type { Person } from './auth/session';
import { TopBar } from './components/TopBar';
import './pages/SignInPage.css';
import { SIGNED_OUT_LANGUAGE, setLanguage, t } from './i18n';
import { LanguageProvider } from './i18n/LanguageProvider';
import { CellarStoreProvider, useCellarStore } from './store/CellarStoreContext';
import { SignInPage } from './pages/SignInPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { InventoryPage } from './pages/InventoryPage';
import { DrinkPage } from './pages/DrinkPage';
import { NewPurchasePage } from './pages/NewPurchasePage';
import { WineDetailPage } from './pages/WineDetailPage';
import { PeopleScreen } from './pages/PeopleScreen';
import { SettingsPage } from './pages/SettingsPage';
import { HomePage } from './pages/HomePage';
import { ExpiringPage } from './pages/ExpiringPage';

// The map carries the world atlas: loaded only when opened (User Story 10).
const MapPage = lazy(() => import('./pages/MapPage'));

/**
 * The app shell (T028): a sign-in gate, then per-cellar routing (T073) and the owner-only people
 * screen (T068). What the app shows here is a convenience; the isolation itself is enforced by
 * the storage permissions (infra/lib/permissions.ts) and the `admin` function's own owner check.
 *
 * - A member only ever opens their own cellar: any other `/c/{id}` shows the plain "not found"
 *   page, the same whether or not that cellar exists.
 * - The owner opens their own cellar, or any cellar in their selector's list. Any other id is
 *   "not found" too, and is never created by opening it.
 * - `/reglages/admin` ("Admin" under "Réglages", owner request 2026-10-01; formerly
 *   `/admin/personnes`, which now leads there) is the people screen for the owner and "not found"
 *   for everyone else.
 */

/** Everything under `/c/:cellarId`, once we know the cellar may be opened. */
function CellarArea({ entry }: { entry: CellarEntry }) {
  return (
    <ActiveCellarProvider value={entry}>
      <CellarStoreProvider cellarId={entry.id} createIfMissing={entry.own}>
        <CellarScreens entry={entry} />
      </CellarStoreProvider>
    </ActiveCellarProvider>
  );
}

function CellarScreens({ entry }: { entry: CellarEntry }) {
  const { failure } = useCellarStore();
  if (failure === 'not-found') {
    return <NotFoundPage />;
  }
  return (
    <>
      <TopBar active={entry} />
      {failure === 'failed' ? (
        <main>
          <p role="alert">{t('cellar.loadFailed')}</p>
        </main>
      ) : (
        <Routes>
          <Route index element={<HomePage />} />
          <Route path="stock" element={<InventoryPage />} />
          <Route path="echeances" element={<ExpiringPage />} />
          <Route path="achat/nouveau" element={<NewPurchasePage />} />
          <Route path="boire" element={<DrinkPage />} />
          <Route path="vin/:wineId" element={<WineDetailPage />} />
          <Route
            path="carte"
            element={
              <Suspense
                fallback={
                  <main>
                    <p>{t('common.loading')}</p>
                  </main>
                }
              >
                <MapPage />
              </Suspense>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      )}
    </>
  );
}

function CellarRoute({ person }: { person: Person }) {
  const { cellarId } = useParams<{ cellarId: string }>();
  const { cellars } = useCellarList();

  if (cellarId === person.id) {
    const own = cellars?.find((entry) => entry.own) ?? { id: person.id, name: '', own: true };
    return <CellarArea key={own.id} entry={own} />;
  }
  if (person.role !== 'owner') {
    return <NotFoundPage />;
  }
  if (!cellars) {
    return (
      <>
        <TopBar />
        <main>
          <p>{t('common.loading')}</p>
        </main>
      </>
    );
  }
  const entry = cellars.find((candidate) => candidate.id === cellarId);
  return entry ? <CellarArea key={entry.id} entry={entry} /> : <NotFoundPage />;
}

function SignedInArea({ person }: { person: Person }) {
  return (
    <CellarListProvider person={person}>
      <LanguageProvider person={person}>
        <Routes>
          <Route path="/c/:cellarId/*" element={<CellarRoute person={person} />} />
          <Route
            path="/reglages/admin"
            element={person.role === 'owner' ? <PeopleScreen /> : <NotFoundPage />}
          />
          {/* The users screen's former address. */}
          <Route path="/admin/personnes" element={<Navigate to="/reglages/admin" replace />} />
          <Route path="/reglages" element={<SettingsPage />} />
          {/* Any other address while signed in (including "/connexion" itself,
            right after signing in) goes home to the person's own cellar. */}
          <Route path="*" element={<Navigate to={`/c/${person.id}`} replace />} />
        </Routes>
      </LanguageProvider>
    </CellarListProvider>
  );
}

/** The pages before sign-in: always in English, whoever opens them (owner, 2026-10-13). */
function SignedOutArea() {
  setLanguage(SIGNED_OUT_LANGUAGE);
  return (
    <Routes>
      <Route path="/connexion" element={<SignInPage />} />
      <Route path="*" element={<Navigate to="/connexion" replace />} />
    </Routes>
  );
}

export function AppRouter() {
  const { person, restoring } = useAuth();

  // While a saved session is being restored (FR-028), show the sign-in background, not the form.
  if (restoring) {
    return <div className="sign-in" aria-busy="true" />;
  }

  return (
    <BrowserRouter>
      {person ? <SignedInArea key={person.id} person={person} /> : <SignedOutArea />}
    </BrowserRouter>
  );
}
